import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router';
import { useDocumentTitle } from '@/shared/lib/use-document-title';
import { useOrgScope } from '../entities/org';
import { CreateOrgDialog } from '../features/org/create-org';
import { Button } from '../shared/ui/button';
import { EmptyState } from '../shared/ui/empty-state';
import { ErrorState } from '../shared/ui/error-state';
import { PageLayout } from '../shared/ui/page-layout';
import { Skeleton } from '../shared/ui/skeleton';

/**
 * `/`: on to the last-used org (a convenience kept in localStorage) or the first membership. Someone with no
 * org (an invited-style signup, or the last org was left) gets an empty state with "Create organization".
 */
export function HomePage() {
  const { t } = useTranslation('org');
  useDocumentTitle(t('home.title'));
  const scope = useOrgScope();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  if (scope.status === 'loading')
    return (
      <PageLayout title={<Skeleton className="h-9 w-64" />}>
        <section aria-busy="true" aria-label={t('home.loading')}>
          <Skeleton className="h-24 w-full" />
        </section>
      </PageLayout>
    );
  if (scope.status === 'error')
    return (
      <PageLayout title={t('home.errorTitle')}>
        <ErrorState
          title={t('home.errorTitle')}
          description={scope.error.message}
          onRetry={scope.retry}
        />
      </PageLayout>
    );
  if (scope.org) return <Navigate to={`/o/${scope.org.slug}`} replace />;
  return (
    <PageLayout title={t('home.title')} width="narrow">
      <EmptyState
        title={t('home.noOrgTitle')}
        description={t('home.noOrgHelp')}
        action={<Button onClick={() => setCreating(true)}>{t('create.submit')}</Button>}
      />
      <CreateOrgDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(org) => navigate(`/o/${org.slug}`)}
      />
    </PageLayout>
  );
}
